import { useEffect, useState } from "react";
import { CONFIG_FIELD_RANGES, CONFIG_ID, ConfigNumberField, StoredConfig } from "../../../types";
import { ApiError, ConfigApi } from "../api";
import { Button, Panel, inputClass } from "./Shell";

const FIELDS: { key: ConfigNumberField; label: string; help: string }[] = [
  {
    key: "captureRetentionDays",
    label: "Capture retention (days)",
    help: "Captures and their breadcrumbs are removed by DynamoDB TTL this many days after they arrive. Applies to captures received from now on (the TTL is stamped at write time); existing ones keep theirs. Groups are never expired.",
  },
  {
    key: "internalRetentionDays",
    label: "log-pile's own error notes retention (days)",
    help: "How long entries on the \"log-pile errors\" tab are kept. Separate from capture retention — these are about log-pile itself, not the services it watches.",
  },
  {
    key: "floodLimitPerMinute",
    label: "Flood limit (hits per group per minute)",
    help: "Past this many hits on one group within a minute, further captures in that minute are counted on the group but their bodies are not stored. The group shows how many were not stored.",
  },
  {
    key: "floodSampleEvery",
    label: "Flood sample (keep every Nth)",
    help: "While a group is over the flood limit, every Nth capture is still stored and marked as a sample, so there is always something recent to look at.",
  },
];

export const Settings = () => {
  const [config, setConfig] = useState<StoredConfig | undefined>();
  const [draft, setDraft] = useState<Record<ConfigNumberField, string>>({ captureRetentionDays: "", internalRetentionDays: "", floodLimitPerMinute: "", floodSampleEvery: "" });
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const apply = (c: StoredConfig) => {
    setConfig(c);
    setDraft({
      captureRetentionDays: String(c.captureRetentionDays),
      internalRetentionDays: String(c.internalRetentionDays),
      floodLimitPerMinute: String(c.floodLimitPerMinute),
      floodSampleEvery: String(c.floodSampleEvery),
    });
  };

  useEffect(() => {
    ConfigApi.get().then(apply).catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load"));
  }, []);

  const parsed = Object.fromEntries(FIELDS.map((f) => [f.key, parseInt(draft[f.key], 10)])) as Record<ConfigNumberField, number>;
  const inRange = (k: ConfigNumberField) => {
    const [min, max] = CONFIG_FIELD_RANGES[k];
    return Number.isInteger(parsed[k]) && parsed[k] >= min && parsed[k] <= max;
  };
  const allValid = FIELDS.every((f) => inRange(f.key));
  const changed = FIELDS.filter((f) => config && parsed[f.key] !== config[f.key]);

  const save = async () => {
    setBusy(true);
    setError(undefined);
    setSaved(false);
    try {
      const patch = Object.fromEntries(changed.map((f) => [f.key, parsed[f.key]]));
      apply(await ConfigApi.update({ id: CONFIG_ID, ...patch }));
      setSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!config && !error) return <p className="text-sm text-neutral-500">Loading…</p>;

  return (
    <Panel className="p-4 space-y-5 max-w-xl">
      <h1 className="font-semibold">Settings</h1>
      {FIELDS.map((f) => {
        const [min, max] = CONFIG_FIELD_RANGES[f.key];
        return (
          <label key={f.key} className="block text-sm space-y-1">
            <span className="text-neutral-500">{f.label}</span>
            <input
              className={`${inputClass} w-32 block ${inRange(f.key) ? "" : "border-red-500"}`}
              type="number"
              min={min}
              max={max}
              value={draft[f.key]}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
            />
            <p className="text-xs text-neutral-500">{f.help}</p>
          </label>
        );
      })}
      <div className="flex items-center gap-3">
        <Button kind="primary" disabled={busy || !allValid || changed.length === 0} onClick={save}>
          {busy ? "Saving…" : "Save"}
        </Button>
        <span className="text-xs text-neutral-500">Changes reach every running ingest Lambda within a minute.</span>
      </div>
      {config?.updatedAt && <p className="text-xs text-neutral-400">Last changed {new Date(config.updatedAt).toLocaleString()}{config.updatedBy ? ` by ${config.updatedBy}` : ""}</p>}
      {saved && <p className="text-sm text-green-700 dark:text-green-400">Saved.</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </Panel>
  );
};
